import {
  ChangeEvent,
  forwardRef,
  isValidElement,
  memo,
  MouseEvent,
  ReactNode,
  useCallback,
  useId,
  useRef,
} from 'react';
import { useControlledState } from '../_core/hooks/use-controlled-state';
import { useFieldRef } from '../_core/hooks/use-field-ref';
import { InputProps } from './input.types';
import {
  Addon,
  AffixWrapper,
  ClearButton,
  CrossIcon,
  Group,
  GroupWrapper,
  InputEl,
  Prefix,
  Suffix,
} from './input.styled';
import { CharCounter } from '../_core/field';

/**
 * Строки/числа оборачиваются в Addon со стандартными стилями (серый фон, рамка).
 * ReactElement (Button, Select и т.д.) рендерится напрямую — компонент стилизует себя сам.
 * Group через CSS-селекторы автоматически убирает border-radius на стыке с инпутом.
 */
const renderAddon = (
  content: ReactNode,
  size: NonNullable<InputProps['size']>
) => {
  if (isValidElement(content)) {
    return content;
  }

  return <Addon $size={size}>{content}</Addon>;
};

/**
 * Универсальный инпут дизайн-системы.
 *
 * Поддерживает:
 * - Размеры: s (40px, default), m (44px)
 * - Статусы: error, warning, success
 * - Prefix / Suffix (контент внутри рамки)
 * - AddonBefore / AddonAfter (контент снаружи рамки)
 * - AllowClear (кнопка очистки)
 * - MaxCount (мягкий лимит со счётчиком)
 */
export const Input = memo(
  forwardRef<HTMLInputElement, InputProps>((props, forwardedRef) => {
    const {
      size = 's',
      status,
      prefix,
      suffix,
      addonBefore,
      addonAfter,
      allowClear = false,
      onClear,
      maxCount,
      disabled,
      readOnly,
      id,
      value,
      defaultValue,
      onChange,
      style,
      className,
      ...restProps
    } = props;

    /*
     * Держим длину, а не значение: в неконтролируемом режиме значение принадлежит
     * DOM, и дублировать его в состоянии незачем — счётчику и крестику нужна
     * только длина.
     */
    const [charCount, setCharCount] = useControlledState(
      value === undefined ? undefined : String(value ?? '').length,
      String(defaultValue ?? '').length
    );

    const counterId = useId();

    const { elementRef: inputRef, attachRef } = useFieldRef(
      forwardedRef,
      (node) => setCharCount(node.value.length)
    );

    const needsCharCount = allowClear || maxCount !== undefined;

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        // Без счётчика и крестика длина никому не нужна — не гоняем ререндер
        if (needsCharCount) {
          setCharCount(e.target.value.length);
        }

        onChange?.(e);
      },
      [needsCharCount, setCharCount, onChange]
    );

    const onClearRef = useRef(onClear);
    onClearRef.current = onClear;

    const handleClear = useCallback(() => {
      const input = inputRef.current;

      if (!input) {
        return;
      }

      input.focus();

      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;

      nativeSetter?.call(input, '');
      input.dispatchEvent(new Event('input', { bubbles: true }));

      onClearRef.current?.();
    }, [inputRef]);

    const handleAffixClick = (event: MouseEvent<HTMLSpanElement>) => {
      // Клик по кнопке в prefix/suffix — её дело, фокус у неё не отбираем
      if (!disabled && !(event.target as HTMLElement).closest('button')) {
        inputRef.current?.focus();
      }
    };

    const hasValue = charCount > 0;
    const isExceeded = maxCount !== undefined && charCount > maxCount;
    const showClear = allowClear && hasValue && !disabled && !readOnly;
    const showCounter = maxCount !== undefined;
    const hasSuffix = !!suffix || allowClear || showCounter;
    const hasAddonBefore = !!addonBefore;
    const hasAddonAfter = !!addonAfter;

    const ariaProps = {
      id,
      'aria-invalid': status === 'error' || isExceeded || undefined,
      'aria-describedby': showCounter ? counterId : undefined,
    };

    const affixedInput = (
      <AffixWrapper
        $size={size}
        $status={status}
        $hasAddonBefore={hasAddonBefore}
        $hasAddonAfter={hasAddonAfter}
        data-disabled={disabled ? '' : undefined}
        data-readonly={readOnly ? '' : undefined}
        onClick={handleAffixClick}
        style={!hasAddonBefore && !hasAddonAfter ? style : undefined}
        className={!hasAddonBefore && !hasAddonAfter ? className : undefined}
      >
        {prefix && <Prefix>{prefix}</Prefix>}

        <InputEl
          ref={attachRef}
          $size={size}
          disabled={disabled}
          readOnly={readOnly}
          {...ariaProps}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          {...restProps}
        />

        {hasSuffix && (
          <Suffix>
            {suffix}

            {showCounter && (
              <CharCounter id={counterId} $exceeded={isExceeded}>
                {charCount}/{maxCount}
              </CharCounter>
            )}

            {allowClear && (
              <ClearButton
                $visible={showClear}
                tabIndex={showClear ? 0 : -1}
                aria-hidden={!showClear}
                type="button"
                aria-label="Очистить"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              >
                <CrossIcon />
              </ClearButton>
            )}
          </Suffix>
        )}
      </AffixWrapper>
    );

    if (!hasAddonBefore && !hasAddonAfter) {
      return affixedInput;
    }

    return (
      <GroupWrapper style={style} className={className}>
        <Group>
          {addonBefore && renderAddon(addonBefore, size)}
          {affixedInput}
          {addonAfter && renderAddon(addonAfter, size)}
        </Group>
      </GroupWrapper>
    );
  })
);

Input.displayName = 'Input';
